import { Injectable, inject } from '@angular/core';
import { TimelineStayGateway } from '@wilson/api/gateway';
import { StayStatus } from '@wilson/interfaces';
import { WilsonState } from '@wilson/non-domain-specific/decorators/wilson-state';
import { endOfDay, startOfDay } from 'date-fns';
import {
  firstValueFrom,
  Observable,
  switchMap,
  catchError,
  throwError,
  of,
} from 'rxjs';

export enum StayCounterColor {
  Green = '#59CA8F',
  Yellow = '#FA8C16',
  Red = '#FF6565',
}
@Injectable()
export class StaysKpiHelperService {
  private readonly timelineStayGateway = inject(TimelineStayGateway);

  @WilsonState<number>(0)
  stayCounter!: number;
  stayCounter$!: Observable<number>;

  @WilsonState<boolean>(false)
  isLoadingCounter!: boolean;
  isLoadingCounter$!: Observable<boolean>;

  @WilsonState<StayCounterColor>(StayCounterColor.Green)
  counterColor!: StayCounterColor;
  counterColor$!: Observable<StayCounterColor>;

  public async setStayCounterForTimeframe(
    timeframe: Interval,
    currentDate: string,
    userIds: string[],
  ) {
    this.isLoadingCounter = true;
    try {
      this.stayCounter = await this.computeStayCount(
        new Date(timeframe.start),
        new Date(timeframe.end),
        userIds,
      );

      if (this.stayCounter === 0) {
        this.counterColor = StayCounterColor.Green;
      } else {
        const stayCounterForCurrentDate = await this.computeStayCount(
          startOfDay(new Date(currentDate)),
          endOfDay(new Date(currentDate)),
          userIds,
        );

        this.counterColor =
          stayCounterForCurrentDate === 0
            ? StayCounterColor.Yellow
            : StayCounterColor.Red;
      }
      this.isLoadingCounter = false;
    } catch {
      this.stayCounter = 0;
      this.counterColor = StayCounterColor.Green;
      this.isLoadingCounter = false;
    }
  }

  private async computeStayCount(
    startDate: Date,
    endDate: Date,
    userIds: string[],
  ) {
    const necessaryStaysForCurrentDate = await this.getStaysStatusInRange(
      {
        start: startDate,
        end: endDate,
      },
      userIds,
    );

    const validatedStaysForTimeFrame = await firstValueFrom(
      this.fetchStayValidations(
        {
          start: startDate,
          end: endDate,
        },
        userIds,
      ),
    );

    return this.countNecessaryStays(
      necessaryStaysForCurrentDate,
      validatedStaysForTimeFrame,
    );
  }

  private countNecessaryStays(
    necessaryStaysInRange: Record<string, boolean>,
    validatedStaysInRange: Record<string, boolean>,
  ): number {
    const necessaryStaysInRangeCount = Object.values(
      necessaryStaysInRange,
    ).filter((value) => value === true).length;

    const invalidStayInRangeCount = Object.values(validatedStaysInRange).filter(
      (value) => value === false,
    ).length;

    return necessaryStaysInRangeCount + invalidStayInRangeCount;
  }

  private getStaysStatusInRange(
    dateRange: {
      start: Date;
      end: Date;
    },
    userIds: string[],
  ) {
    return firstValueFrom(
      this.timelineStayGateway.fetchNecessaryStaysInRange({
        dateRange,
        userIds,
      }),
    );
  }

  private fetchStayValidations(
    dateRange: {
      start: Date;
      end: Date;
    },
    userIds: string[],
  ) {
    try {
      return this.timelineStayGateway
        .getStays({
          startDatetime: dateRange.start.toISOString(),
          endDatetime: dateRange.end.toISOString(),
          statuses: [
            StayStatus.Open,
            StayStatus.Requested,
            StayStatus.Confirmed,
            StayStatus.Invoiced,
          ],
          userIds,
        })
        .pipe(
          switchMap((stays) => {
            const stayIds: string[] = stays.map((stay) => stay.id);
            if (stayIds.length) {
              return this.timelineStayGateway.validateStays(stayIds);
            } else {
              return of({});
            }
          }),
          catchError((error) => {
            console.error(
              'An error occurred while refreshing user stays validations in range:',
              error,
            );
            return throwError(() => error);
          }),
        );
    } catch (error) {
      console.error(
        'An error occurred while preparing the date range for validation:',
        error,
      );

      throw error;
    }
  }
}
